import * as Sentry from '@sentry/react';
import { LogMeta, Severity } from './types';

const sentryRelease = import.meta.env.VITE_APP_BUILD_SHA || undefined;

Sentry.init({
  dsn: 'https://9abe55543255dfe358a49c02c7319552@o4507376227713024.ingest.us.sentry.io/4507647570935808',
  enabled: process.env.NODE_ENV === 'development' ? false : true,
  debug: process.env.NODE_ENV === 'development' ? true : false,
  enableLogs: true,
  release: sentryRelease,
  beforeSend: (event, hint) => {
    if (isChunkLoadError(hint.originalException)) {
      return null;
    }
    if (isFirebaseAuthNetworkError(hint.originalException)) {
      return null;
    }
    if (isStorageQuotaExceededError(hint.originalException)) {
      return null;
    }
    if (isFirebaseDeadlineExceededError(hint.originalException)) {
      return null;
    }
    if (isFirebaseFunctionsInternalError(hint.originalException)) {
      return null;
    }
    return event;
  },
  environment:
    process.env.NODE_ENV == 'development' ? 'development' : 'production',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  tracePropagationTargets: ['localhost', /^\/api(\/|$)/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

export const isChunkLoadError = (error: any) => {
  return (
    error?.name === 'ChunkLoadError' ||
    error?.message?.includes('Failed to fetch dynamically imported module') ||
    error?.message?.includes('error loading dynamically imported module') ||
    error?.message?.includes('Loading chunk') ||
    error?.message?.includes('Loading CSS chunk') ||
    error?.message?.includes('ChunkLoadError') ||
    error?.message?.includes('Unable to preload') ||
    // Safari surfaces a missing chunk (SPA fallback serves index.html) as a
    // MIME-type error instead of a fetch failure — same stale-deploy root
    // cause as the other patterns above.
    error?.message?.includes('is not a valid JavaScript MIME type')
  );
};

// Firebase Auth raises `auth/network-request-failed` when an internal token
// refresh (`getIdToken`) cannot reach the STS endpoint. The next call that
// needs a token will refresh again, so a single transient blip does not
// require user-visible recovery and is not actionable in Sentry.
export const isFirebaseAuthNetworkError = (error: any) => {
  return error?.code === 'auth/network-request-failed';
};

// Firebase Analytics and other Firebase SDKs write to IndexedDB; when the
// user's browser storage is exhausted those writes raise a DOMException with
// name `QuotaExceededError` that bubbles up as an unhandled rejection. It's a
// user-environment problem, not an app bug, so drop it.
export const isStorageQuotaExceededError = (error: any) => {
  return error?.name === 'QuotaExceededError';
};

// Firestore raises `FirebaseError: deadline-exceeded` when a watch/listen
// stream or query cannot complete within the SDK's deadline (slow network,
// backgrounded tab, flaky connection). The client retries on its own and
// React Query will re-fetch when the user comes back, so these are not
// actionable in Sentry. Callable Functions surface the same condition as
// `functions/deadline-exceeded`.
//
// The `name === 'FirebaseError'` guard is load-bearing: HTTP backend errors
// arrive as `ApiHttpError` with native codes (for example
// `deadline-exceeded`) and must keep flowing to Sentry.
export const isFirebaseDeadlineExceededError = (error: any) => {
  return (
    error?.name === 'FirebaseError' &&
    (error.code === 'deadline-exceeded' ||
      error.code === 'functions/deadline-exceeded')
  );
};

// Firebase's callable transport surfaces unexpected server-side failures as
// `FirebaseError` with code `functions/internal` — typically a retired or
// renamed callable that an old cached client is still trying to reach, a
// transport blip mid-invocation, or a 5xx without a recognized error code.
// All three are unactionable in Sentry: the retired-callable case is by
// design (the new code uses the `api`/`api-stream` transports instead) and
// the transient cases are recovered by the next user retry or by the
// caller's own fallback path (e.g. `useScoutChat` falls back through
// `openaiChatNonStreaming` → v8 compat). Real internal errors inside a
// still-live callable are visible in Cloud Functions logs.
//
// The `name === 'FirebaseError'` guard matches the rationale on
// `isFirebaseDeadlineExceededError`: HTTP backend failures arrive as
// `ApiHttpError` instances and must keep flowing to Sentry.
export const isFirebaseFunctionsInternalError = (error: any) => {
  return error?.name === 'FirebaseError' && error.code === 'functions/internal';
};

const toSentryLevel = (s: Severity): Sentry.SeverityLevel =>
  s === 'ERROR'
    ? 'error'
    : s === 'WARNING'
      ? 'warning'
      : s === 'INFO'
        ? 'info'
        : 'debug';

/**
 * Captures an exception and sends it to Sentry.
 */
export const captureSentryException = Sentry.captureException;

/**
 * Adds a breadcrumb that will be attached to any future exceptions.
 *
 * @warning Do not use this directly. Use telemetry.info, telemetry.warn, or telemetry.error instead.
 *
 * @description
 * When an exception is reported to Sentry, a list of breadcrumbs leading up to the exception will
 * be shown on the exception's "issue" page.
 *
 * This helps us understand the steps needed to reproduce the error.
 *
 * For more info and example screenshots, see:
 * https://docs.sentry.io/product/issues/issue-details/breadcrumbs/
 */
export const sendSentryBreadcrumb = (props: {
  message: string;
  severity: Severity;
  extra?: LogMeta;
}) => {
  const { message, severity, extra } = props;
  Sentry.addBreadcrumb({
    message,
    level: toSentryLevel(severity),
    data: extra,
  });
};

const isSentryLogAttribute = (
  value: unknown,
): value is string | number | boolean => {
  const valueType = typeof value;
  return (
    valueType === 'string' || valueType === 'number' || valueType === 'boolean'
  );
};

const blockedAttributeKeyPattern =
  /token|authorization|password|secret|cookie/i;

const blockedNestedAttributeKeyPattern =
  /email|displayName|url|body|html|formData/i;

const safeNestedAttributeKeyPattern =
  /(^|[A-Z])(id|ids|uid|uuid)$|tenantId|status|type|route|path|code|count|total|size|length|attempt|outcome|reason|duration|latency|usedJSHeapSize|jsHeapSizeLimit/i;

// Keep existing top-level primitive fields searchable, but only flatten nested
// operational context when the leaf key is allow-listed.
const toSentryLogAttributeEntries = (
  value: unknown,
  path: string[],
): [string, string | number | boolean][] => {
  const [key] = path.slice(-1);
  const hasBlockedNestedAttributeKey = path.some((pathKey) =>
    blockedNestedAttributeKeyPattern.test(pathKey),
  );

  if (path.some((pathKey) => blockedAttributeKeyPattern.test(pathKey))) {
    return [];
  }

  if (path.length > 1 && hasBlockedNestedAttributeKey) {
    return [];
  }

  if (isSentryLogAttribute(value)) {
    if (
      path.length === 1 ||
      safeNestedAttributeKeyPattern.test(key) ||
      (path[0] === 'userIds' && (key === 'auth' || key === 'firestore'))
    ) {
      return [[path.join('.'), value]];
    }
    return [];
  }

  if (hasBlockedNestedAttributeKey) {
    return [];
  }

  if (value instanceof Error) {
    return [
      [`${path.join('.')}.name`, value.name],
      [`${path.join('.')}.message`, value.message],
    ];
  }

  if (value === null || typeof value !== 'object' || path.length >= 3) {
    return [];
  }

  return Object.entries(value).flatMap(([nestedKey, nestedValue]) =>
    toSentryLogAttributeEntries(nestedValue, [...path, nestedKey]),
  );
};

const toSentryLogAttributes = (extra?: LogMeta) => {
  return Object.fromEntries(
    Object.entries(extra ?? {}).flatMap(([key, value]) =>
      toSentryLogAttributeEntries(value, [key]),
    ),
  );
};

const sentryLogBySeverity = {
  DEBUG: 'debug',
  INFO: 'info',
  WARNING: 'warn',
  ERROR: 'error',
} satisfies Record<Severity, 'debug' | 'info' | 'warn' | 'error'>;

export const sendSentryLog = (props: {
  message: string;
  severity: Severity;
  extra?: LogMeta;
}) => {
  const { message, severity, extra } = props;
  Sentry.logger[sentryLogBySeverity[severity]](
    message,
    toSentryLogAttributes(extra),
  );
};

export const setSentryUserProperties = (props: {
  userIds: { auth: string; firestore: string };
  email: string;
  tenantId: string;
}) => {
  const { userIds, email, tenantId } = props;
  Sentry.setUser({
    // Using auth ID as the "official" user ID here because it's easiest to access
    // in other places, and so is easiest to correlate with other debug logs.
    id: userIds.auth,
    firestoreId: userIds.firestore,
    email,
    tenantId,
  });
};

export const clearSentryUserProperties = () => {
  Sentry.setUser(null);
};
